import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCircleDot,
    faFont,
    faPlus,
    faSquareCheck,
    faStar,
    type IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import {
    Button,
    ButtonGroup,
    Popover,
    PopoverContent,
    PopoverTitle,
    PopoverTrigger,
} from "frontend-components";
import { QuestionType, type PartialCreateQuestion } from "@pulse/api/surveyApi";
import { useCreateSurvey } from "../../hooks";

type QuestionTypeButton = {
    icon: IconDefinition;
    labelKey: string;
    buildQuestion: (t: TFunction) => PartialCreateQuestion;
};

const defaultOptions = (t: TFunction) => [
    t("question.multipleChoice.defaultOption", { count: 1 }),
    t("question.multipleChoice.defaultOption", { count: 2 }),
];

const QUESTION_TYPE_BUTTONS: QuestionTypeButton[] = [
    {
        icon: faStar,
        labelKey: "question.type.rating",
        buildQuestion: () => ({ questionType: QuestionType.RATING }),
    },
    {
        icon: faFont,
        labelKey: "question.type.text",
        buildQuestion: () => ({ questionType: QuestionType.TEXT }),
    },
    {
        icon: faCircleDot,
        labelKey: "question.type.multipleChoice",
        buildQuestion: (t) => ({
            questionType: QuestionType.MULTIPLE_CHOICE,
            questionOptions: defaultOptions(t),
        }),
    },
    {
        icon: faSquareCheck,
        labelKey: "question.type.multiSelect",
        buildQuestion: (t) => ({
            questionType: QuestionType.MULTI_SELECT,
            questionOptions: defaultOptions(t),
        }),
    },
];

export function AddQuestionTypeButtons() {
    const { t } = useTranslation();
    const { createQuestion, isPending } = useCreateSurvey();
    const [open, setOpen] = useState(false);

    const addQuestion = (buildQuestion: QuestionTypeButton["buildQuestion"]) => {
        void createQuestion(buildQuestion(t));
        setOpen(false);
    };

    return (
        <>
            {/* The four types do not fit on a phone — offer them behind a single button. */}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                    render={
                        <Button
                            type="button"
                            variant="arrow"
                            size="arrow"
                            className="sm:hidden"
                            disabled={isPending}
                            aria-label={t("question.selectType")}
                        />
                    }
                >
                    <FontAwesomeIcon icon={faPlus} />
                </PopoverTrigger>
                <PopoverContent className="w-56 gap-2 p-2">
                    <PopoverTitle className="px-2 text-compendia-muted text-sm">
                        {t("question.selectTypeHeader")}
                    </PopoverTitle>
                    <div className="flex flex-col">
                        {QUESTION_TYPE_BUTTONS.map(({ icon, labelKey, buildQuestion }) => (
                            <Button
                                key={labelKey}
                                type="button"
                                variant="ghost"
                                className="hover:bg-compendia-greige-20 justify-start gap-3 px-3"
                                disabled={isPending}
                                onClick={() => addQuestion(buildQuestion)}
                            >
                                <FontAwesomeIcon icon={icon} />
                                <span>{t(labelKey)}</span>
                            </Button>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>

            <ButtonGroup className="border-compendia-greige-20 rounded-card hidden max-w-full overflow-x-auto border p-1 shadow-md sm:flex">
                {QUESTION_TYPE_BUTTONS.map(({ icon, labelKey, buildQuestion }) => (
                    <Button
                        key={labelKey}
                        type="button"
                        variant="ghost"
                        disabled={isPending}
                        className="hover:bg-compendia-greige-20 shrink-0 gap-1 px-2 sm:gap-2 sm:px-5"
                        onClick={() => addQuestion(buildQuestion)}
                    >
                        <FontAwesomeIcon icon={icon} /> {t(labelKey)}
                    </Button>
                ))}
            </ButtonGroup>
        </>
    );
}
